import {useState} from 'react';
import {useForm} from 'react-hook-form';
import {useNavigate, useLocation, Link} from 'react-router-dom';
import {useAuthStore, hasPortalAccessProfile} from '@common-web/stores/authStore';
import {usePortal} from '@common-web/providers/portalProvider';
import {userMatchesPortal} from '@common-web/lib/portal';
import {toast} from '@common-web/hooks/use-toast';
import {Button} from '@common-web/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@common-web/components/ui/card';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {authClient} from '@common-web/auth';
import {resolvePostLoginRedirect} from '@common-web/lib/postLoginRedirect';
import {LogIn, Mail, Lock, ArrowRight} from 'lucide-react';
import {AuthPageLayout} from '@common-web/components/auth-page-layout';
import {INVALID_CREDENTIALS_MESSAGE, PORTAL_TYPE_HEADER} from '@common/constants/auth';
import {HTTP_STATUS} from '@common/constants/http';
import type {BetterAuthUser} from '@api/auth';

/**
 * TODO: delete this file.
 * https://app.asana.com/1/1206214889537900/project/1206881612175705/task/1218552106825605?focus=true
 *
 * Only `apps/internal` renders it. `@client/pages/public/LoginPage` is the
 * newer branded equivalent used by every client portal, and the internal portal
 * is due to move onto it along with the other three auth pages here
 * (`signupPage`, `forgotPasswordPage`, `resetPasswordPage`), each of which has
 * a branded twin in `@client/pages/public/`.
 *
 * Until that lands this file is live, not dead: the sign-in logic below is
 * duplicated line-for-line in the branded page, so **any auth change has to be
 * made in both**. That duplication is the reason the ticket exists.
 */
interface LoginFormData {
  email: string;
  password: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as {from?: {pathname: string}} | null)?.from?.pathname;
  const portal = usePortal();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: {errors},
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);

    try {
      const result = await authClient.signIn.email(
        {
          email: data.email,
          password: data.password,
        },
        // Tell the API which portal is asking. The server refuses to mint a
        // session for a portal the account cannot use, so the checks below are
        // defence in depth rather than the boundary itself.
        {headers: {[PORTAL_TYPE_HEADER]: portal.portalType}},
      );

      if (result.error) {
        // Every sign-in failure reads the same. Distinguishing "wrong password"
        // from "valid password, wrong portal" from "no such account" tells an
        // anonymous visitor which accounts exist and which passwords are real.
        // Rate limiting is about the caller rather than the account, so it may
        // speak for itself.
        toast({
          title: 'Error',
          description:
            result.error.status === HTTP_STATUS.TOO_MANY_REQUESTS && result.error.message
              ? result.error.message
              : INVALID_CREDENTIALS_MESSAGE,
          variant: 'destructive',
        });
      } else if (result.data) {
        const sessionResult = await authClient.getSession();
        if (sessionResult.data) {
          const failClosed = async () => {
            await authClient.signOut();
            toast({
              title: 'Error',
              description: INVALID_CREDENTIALS_MESSAGE,
              variant: 'destructive',
            });
          };

          if (!hasPortalAccessProfile(sessionResult.data.user)) {
            await failClosed();
            return;
          }

          if (!userMatchesPortal(sessionResult.data.user, portal)) {
            await failClosed();
            return;
          }

          setAuth(sessionResult.data.user, sessionResult.data.session);
          toast({
            title: 'Success',
            description: 'Logged in successfully',
          });

          const user = sessionResult.data.user as BetterAuthUser;
          const {errorMessage, redirectTo} = resolvePostLoginRedirect(user, returnTo);

          if (errorMessage) {
            toast({title: 'Error', description: errorMessage});
            return;
          }
          if (redirectTo) {
            navigate(redirectTo);
          }
        }
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Login failed',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageLayout>
      <div className='w-full max-w-md'>
        <Card className='border-gray-200 shadow-xl'>
          <CardHeader className='space-y-1 pb-6'>
            <div className='flex items-center justify-center mb-4'>
              <div className='p-3 bg-brand-navy/10 rounded-full'>
                <LogIn className='h-6 w-6 text-brand-navy' />
              </div>
            </div>
            <CardTitle className='text-2xl text-center font-bold text-brand-navy'>
              Sign In to Your Account
            </CardTitle>
            <CardDescription className='text-center text-brand-gray'>
              Enter your credentials to access your dashboard
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='flex flex-col gap-5'>
                <div className='space-y-2'>
                  <Label htmlFor='email' className='text-brand-navy font-medium'>
                    Email Address
                  </Label>
                  <div className='relative'>
                    <Mail className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray' />
                    <Input
                      id='email'
                      type='email'
                      placeholder='you@example.com'
                      className='pl-10 h-11 border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                      disabled={isLoading}
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Invalid email address',
                        },
                      })}
                    />
                  </div>
                  {errors.email && (
                    <p className='text-sm text-red-600 mt-1'>{errors.email.message}</p>
                  )}
                </div>

                <div className='space-y-2'>
                  <div className='flex items-center justify-between'>
                    <Label htmlFor='password' className='text-brand-navy font-medium'>
                      Password
                    </Label>
                    <Link
                      to='/forgot-password'
                      className='text-sm text-brand-blue hover:text-brand-blue-dark font-medium transition-colors'
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className='relative'>
                    <Lock className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray' />
                    <Input
                      id='password'
                      type='password'
                      placeholder='••••••••'
                      className='pl-10 h-11 border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                      disabled={isLoading}
                      {...register('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 8,
                          message: 'Password must be at least 8 characters',
                        },
                      })}
                    />
                  </div>
                  {errors.password && (
                    <p className='text-sm text-red-600 mt-1'>{errors.password.message}</p>
                  )}
                </div>

                <Button
                  type='submit'
                  className='w-full h-11 bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold transition-colors shadow-sm'
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className='flex items-center gap-2'>
                      <div className='w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin' />
                      Signing in...
                    </span>
                  ) : (
                    <span className='flex items-center justify-center gap-2'>
                      Sign In
                      <ArrowRight className='h-4 w-4' />
                    </span>
                  )}
                </Button>
              </div>

              <div className='mt-6 text-center'>
                <p className='text-sm text-brand-gray'>
                  Don't have an account?{' '}
                  <Link
                    to='/signup'
                    className='text-brand-blue hover:text-brand-blue-dark font-semibold transition-colors'
                  >
                    Create an account
                  </Link>
                </p>
              </div>
            </form>
          </CardContent>
        </Card>

        <p className='text-center text-xs text-brand-gray mt-6'>
          Protected by enterprise-grade security. Your data is encrypted and secure.
        </p>
      </div>
    </AuthPageLayout>
  );
}
